import React from "react";
import MaskedLines from "./MaskedLines";

/**
 * The heading cluster that opens every major section: a numbered eyebrow, a
 * hairline rule, then the headline.
 *
 * Keeping it in one component is what makes the page feel like a single
 * document rather than a stack of unrelated blocks — the eyebrow, rule and
 * headline always sit in the same relationship to each other.
 *
 * @param {Object} props
 * @param {string} props.index            Two-digit ordinal, e.g. "01".
 * @param {string} props.eyebrow          Short section name, e.g. "Studio".
 * @param {string[]} [props.headlineLines] Headline split into visual lines.
 * @param {React.ReactNode} [props.aside]  Optional right-hand content in the eyebrow row.
 * @param {string} [props.headlineClassName="t-headline"]
 * @param {string} [props.id]             Anchor target for in-page navigation.
 * @param {React.ReactNode} [props.children] Extra content below the headline.
 * @returns {JSX.Element}
 */
function SectionHeading({
  index,
  eyebrow,
  headlineLines,
  aside,
  headlineClassName = "t-headline",
  id,
  children,
}) {
  return (
    <header className="section-head" data-reveal="rise-sm" id={id}>
      <div className="section-head__eyebrow">
        <span className="t-label t-numeral section-head__index">{index}</span>
        <span className="t-label">{eyebrow}</span>
        {aside ? <span className="section-head__aside t-label">{aside}</span> : null}
      </div>

      {headlineLines ? (
        <MaskedLines
          as="h2"
          lines={headlineLines}
          className={headlineClassName}
          staggerMs={90}
        />
      ) : null}

      {children}
    </header>
  );
}

export default SectionHeading;
